import React, { useEffect, useState, useContext } from 'react';
import {
  Box,
  Paper,
  Typography,
  FormControl,
  InputLabel,
  Select,
  MenuItem,
  TextField,
  Checkbox,
  FormControlLabel,
  OutlinedInput,
  Chip,
  CircularProgress,
  Button
} from '@mui/material';
import axios from 'axios';
import { ReportContext } from '../../Context';
import DescriptionIcon from '@mui/icons-material/Description';
import { Document, Packer, Paragraph, TextRun, AlignmentType, Header, BorderStyle, Table, TableRow, TableCell, WidthType, ImageRun } from 'docx';
import { saveAs } from 'file-saver';

// Formulaire "Visite domiciliaire" (sans génération de document pour l'instant)
function CensentSearch() {
  const { report } = useContext(ReportContext);

  const [loading, setLoading] = useState(true);
  const [reportData, setReportData] = useState(null);
  const [settings, setSettings] = useState(null);
  const [intervenants, setIntervenants] = useState([]);
  const [personnesPhysiques, setPersonnesPhysiques] = useState([]);
  const [faits, setFaits] = useState([]);
  const [selectedFaits, setSelectedFaits] = useState([]);

  const [personneVisitee, setPersonneVisitee] = useState('');
  const [personneVisiteeData, setPersonneVisiteeData] = useState(null);
  const [policiersPresents, setPoliciersPresents] = useState([]);

  const [dateVisite, setDateVisite] = useState('');

  const [lieuDifferent, setLieuDifferent] = useState(false);
  const [adresseVisitee, setAdresseVisitee] = useState('');

  const [hasLimitation, setHasLimitation] = useState(false);
  const [limitation, setLimitation] = useState('en se limitant à ');

  const [personneVisiteeType, setPersonneVisiteeType] = useState('');
  const [personneVisiteeTypeCustom, setPersonneVisiteeTypeCustom] = useState('');

  const [butText, setButText] = useState("afin d'y procéder à la recherche / saisie de tous les objets utiles à l’enquête, à savoir : ");

  const isFormComplete =
    !!personneVisiteeData &&
    policiersPresents.length > 0 &&
    !!personneVisiteeType &&
    (personneVisiteeType !== 'custom' || personneVisiteeTypeCustom.trim() !== '') &&
    (!lieuDifferent || adresseVisitee.trim() !== '') &&
    (!hasLimitation || limitation.trim() !== '') &&
    !!settings &&
    !!reportData;

  const formatDateTimeLocal = (date) => {
    const y = date.getFullYear();
    const m = String(date.getMonth() + 1).padStart(2, '0');
    const d = String(date.getDate()).padStart(2, '0');
    const h = String(date.getHours()).padStart(2, '0');
    const min = String(date.getMinutes()).padStart(2, '0');
    return `${y}-${m}-${d}T${h}:${min}`;
  };

  const formatDateShort = (dateString) => {
    const date = new Date(dateString);
    if (Number.isNaN(date.getTime())) return '';
    const jour = String(date.getDate()).padStart(2, '0');
    const mois = String(date.getMonth() + 1).padStart(2, '0');
    const annee = date.getFullYear();
    return `${jour}/${mois}/${annee}`;
  };

  const formatDateLongMonth = (dateString) => {
    const date = new Date(dateString);
    if (Number.isNaN(date.getTime())) return '';
    const mois = [
      'janvier',
      'février',
      'mars',
      'avril',
      'mai',
      'juin',
      'juillet',
      'août',
      'septembre',
      'octobre',
      'novembre',
      'décembre'
    ];
    const jour = String(date.getDate()).padStart(2, '0');
    const moisNom = mois[date.getMonth()];
    const annee = date.getFullYear();
    return `${jour} ${moisNom} ${annee}`;
  };

  const extractDateTimeParts = (value) => {
    if (!value) return null;
    const date = new Date(value);
    if (Number.isNaN(date.getTime())) return null;
    const hours = String(date.getHours()).padStart(2, '0');
    const minutes = String(date.getMinutes()).padStart(2, '0');
    return {
      dateText: formatDateLongMonth(value),
      timeText: `${hours}:${minutes}`,
      isBoldTime: date.getHours() >= 2,
      hourLabel: date.getHours() >= 2 ? 'heures' : 'heure'
    };
  };

  const formatDateTime = (dateString) => {
    if (!dateString) return '';
    const date = new Date(dateString);
    if (isNaN(date.getTime())) return dateString;
    const jour = date.toLocaleDateString('fr-FR', {
      weekday: 'long',
      day: 'numeric',
      month: 'long',
      year: 'numeric'
    });
    const heures = String(date.getHours()).padStart(2, '0');
    const minutes = String(date.getMinutes()).padStart(2, '0');
    const pluriel = date.getHours() >= 2 ? 's' : '';
    return `${jour} à ${heures}:${minutes} heure${pluriel}`;
  };

  const formatFaitDateDescription = (fait) => {
    if (!fait) return '';
    const formatDate = (value) => formatDateTime(value);
    if (fait.timeType === 'range') {
      return `entre le ${formatDate(fait.rangeLowDateTime)} et le ${formatDate(fait.rangeHighDateTime)}`;
    }
    if (fait.fixedDateTime) {
      return `le ${formatDate(fait.fixedDateTime)}`;
    }
    return '';
  };

  const svgTextToPngBase64 = (svgText, width = 150, height = 150) => {
    return new Promise((resolve, reject) => {
      const img = new Image();

      img.onload = () => {
        const canvas = document.createElement('canvas');
        canvas.width = width;
        canvas.height = height;
        const ctx = canvas.getContext('2d');

        ctx.fillStyle = '#ffffff';
        ctx.fillRect(0, 0, width, height);
        ctx.drawImage(img, 0, 0, width, height);
        const base64 = canvas.toDataURL('image/png').split(',')[1];
        resolve(base64);
      };

      img.onerror = reject;

      const svgBlob = new Blob([svgText], { type: 'image/svg+xml;charset=utf-8' });
      const url = URL.createObjectURL(svgBlob);
      img.src = url;
    });
  };

  const base64ToUint8Array = (base64) => {
    const binaryString = atob(base64);
    const bytes = new Uint8Array(binaryString.length);
    for (let i = 0; i < binaryString.length; i++) {
      bytes[i] = binaryString.charCodeAt(i);
    }
    return bytes;
  };

  const loadLogoPng = async () => {
    const svgContent = `<?xml version="1.0" encoding="utf-8"?>
<svg xmlns="http://www.w3.org/2000/svg" viewBox="0 0 150 150" width="150" height="150">
  <circle cx="75" cy="75" r="74" fill="#FFFFFF" stroke="#244BA6" stroke-width="2"/>
  <path fill="#244BA6" d="M150,75c0-41.4-33.6-75-75-75S0,33.6,0,75c0,39.5,30.5,71.9,69.3,74.8c-3.3-4-7.4-9.5-11-14.6
    c-6.1-8.6-13.4-21.5-13.7-34.4c-0.2-9.6,2-14.8,14.6-34.7c4.9-7.8,8.2-21.1,7.7-23.9c-0.5-2.9,4.6,4.3,6.5,9.1
    c2.5,6.4,2.2,11.2-2.5,24.4c-2.2,6.3-5.9,16.4-2.2,20.9c-0.4-0.5,11.9-13.2,14.8-26.2c2.2-10,1.3-23.3-3-33.1
    c-3.8-8.6-8.3-15.7-7.4-27.1c0.4-4.8,1.7-9.4,1.8-9.7c2.8,7,4,9.5,5.1,11.5c5.1,9.6,15.6,23.7,20.5,33.4
    c5,9.6,8.8,25.5,3,41.7c-5.7,15.9-11.1,21.3-16.9,34.2c-4.3,9.6-7.9,21.4-9.7,28.8C117.5,149,150,115.9,150,75"/>
</svg>`;

    try {
      const base64 = await svgTextToPngBase64(svgContent, 100, 100);
      return base64ToUint8Array(base64);
    } catch (error) {
      console.warn('Erreur conversion logo:', error);
      return null;
    }
  };

  const buildReportNumber = () => {
    if (!reportData || !settings) return 'XXXXXX';

    const shortJudicialDistrict = settings.shortJudicialDistrict || '__';
    const indice = reportData.indice || '__';
    const judicialDivision = settings.judicialDivision || '__';
    const number = report?.number || '__';
    const year = report?.year || '__';

    return `${shortJudicialDistrict}.${indice}.${judicialDivision}.${number}/${year}`;
  };

  const convertGrade = (gradeCode) => {
    const gradeMapping = {
      AAGP: 'Aspirant Agent de Police',
      AGP: 'Agent de Police',
      '1AGP': 'Premier Agent de Police',
      ADAGP: 'Aspirant Agent de sécurisation de Police',
      DAGP: 'Agent de sécurisation de Police',
      '1DAGP': 'Premier Agent de sécurisation de Police',
      ABASP: 'Aspirant Assistant de Sécurisation de Police',
      BASP: 'Assistant de Sécurisation de Police',
      '1BASP': 'Premier Assistant de Sécurisation de Police',
      BCSP: 'Coordonnateur de Sécurisation de Police',
      '1BCSP': 'Premier Coordonnateur de Sécurisation de Police',
      AINP: 'Aspirant Inspecteur de Police',
      INP: 'Inspecteur de Police',
      '1INP': 'Premier Inspecteur de Police',
      AINPP: 'Aspirant Inspecteur Principal de Police',
      INPP: 'Inspecteur Principal de Police',
      '1INPP': 'Premier Inspecteur Principal de Police',
      ACP: 'Aspirant Commissaire de Police',
      CP: 'Commissaire de Police',
      '1CP': 'Premier Commissaire de Police',
      CDP: 'Commissaire Divisionnaire de Police',
      '1CDP': 'Premier Commissaire Divisionnaire de Police'
    };
    return gradeMapping[gradeCode] || gradeCode;
  };

  useEffect(() => {
    const fetchData = async () => {
      if (!report || !report.number || !report.year) {
        setLoading(false);
        return;
      }

      try {
        setLoading(true);
        const token = sessionStorage.getItem('authToken');

        const reportResponse = await axios.get(
          `/api/report/search?number=${report.number}&year=${report.year}`,
          { headers: { Authorization: `Bearer ${token}` } }
        );

        const settingsResponse = await axios.get('/api/settings');

        setReportData(reportResponse.data);
        setSettings(settingsResponse.data);

        const allUsersResponse = await axios.get(
          '/api/user',
          { headers: { Authorization: `Bearer ${token}` } }
        );

        const reportIntervenants = reportResponse.data.intervenants || [];
        const allUsers = allUsersResponse.data || [];

        const intervenantsOptions = reportIntervenants.map((i) => ({
          value: `${i.rank} ${i.lastname.toUpperCase()} ${i.firstname}`,
          data: i,
          isReportIntervenant: true
        })).sort((a, b) => a.value.localeCompare(b.value, 'fr', { sensitivity: 'accent' }));

        const allOptions = [...intervenantsOptions];

        if (allUsers.length > 0) {
          allOptions.push({
            value: '--- Autres collègues ---',
            data: null,
            isSeparator: true
          });

          const reportIntervenantIds = reportIntervenants.map(i => i.userId).filter(Boolean);
          const otherUsers = allUsers.filter(user => !reportIntervenantIds.includes(user._id));

          const usersOptions = otherUsers.map(user => {
            const grade = convertGrade(user.rank || '');
            const displayText = `${grade} ${user.lastname.toUpperCase()} ${user.firstname}`;
            return {
              value: displayText,
              data: {
                isExternal: false,
                sign: false,
                rank: grade,
                lastname: user.lastname,
                firstname: user.firstname,
                userId: user._id
              },
              isOtherUser: true
            };
          }).sort((a, b) => a.value.localeCompare(b.value, 'fr', { sensitivity: 'accent' }));

          allOptions.push(...usersOptions);
        }

        setIntervenants(allOptions);

        const personnesFormatted = (reportResponse.data.personnesPhysiques || [])
          .filter(p => p.lastname && !p.isUnknown)
          .map(p => ({
            value: `${p.lastname.toUpperCase()} ${p.firstname || ''}`,
            data: p
          }));

        setPersonnesPhysiques(personnesFormatted);

        const faitsOptions = (reportResponse.data.faits || []).map(f => ({
          value: f._id || `${Math.random()}`,
          data: f,
          label: f.fait || 'Fait non spécifié'
        }));
        setFaits(faitsOptions);

        // Le champ date de la visite est facultatif, on ne l'initialise pas

        setLoading(false);
      } catch (error) {
        console.error('Erreur lors du chargement des données:', error);
        setLoading(false);
      }
    };

    fetchData();
  }, [report]);

  if (loading) {
    return (
      <Box sx={{ p: 2, display: 'flex', justifyContent: 'center', alignItems: 'center' }}>
        <CircularProgress />
      </Box>
    );
  }

  return (
    <Box sx={{ p: 0 }}>
      <Paper sx={{ p: 3 }}>
        <Typography variant="body2" color="text.secondary" sx={{ mb: 3 }}>
        Complétez les informations ci-dessous pour générer le formulaire de visite domiciliaire sur consentement.
        </Typography>

        <Box component="form" sx={{ display: 'flex', flexDirection: 'column', gap: 2 }}>
          <TextField
            fullWidth
            label="Date de la visite (facultatif)"
            type="date"
            value={dateVisite}
            onChange={(e) => setDateVisite(e.target.value)}
            InputLabelProps={{ shrink: true }}
          />

          <FormControl fullWidth required>
            <InputLabel id="personne-visitee-label">Personne qui signe</InputLabel>
            <Select
              labelId="personne-visitee-label"
              value={personneVisitee}
              label="Personne qui signe"
              onChange={(e) => {
                const value = e.target.value;
                setPersonneVisitee(value);
                const selected = personnesPhysiques.find((p) => p.value === value);
                setPersonneVisiteeData(selected?.data || null);
              }}
            >
              {personnesPhysiques.map((personne, index) => (
                <MenuItem key={index} value={personne.value}>
                  {personne.value}
                </MenuItem>
              ))}
            </Select>
          </FormControl>

          <FormControl fullWidth required>
            <InputLabel id="personne-visitee-type-label">Qualité de la personne</InputLabel>
            <Select
              labelId="personne-visitee-type-label"
              value={personneVisiteeType}
              label="Qualité de la personne"
              onChange={(e) => {
                const value = e.target.value;
                setPersonneVisiteeType(value);
                if (value !== 'custom') {
                  setPersonneVisiteeTypeCustom('');
                }
              }}
            >
              <MenuItem value="Majeur domiciliée">Majeur domiciliée</MenuItem>
              <MenuItem value="Majeur non domiciliée mais résidant">Majeur non domiciliée mais résidant</MenuItem>
              <MenuItem value="Mineur sans majeur (kot)">Mineur sans majeur (kot)</MenuItem>
              <MenuItem value="Chef de famille">Chef de famille</MenuItem>
              <MenuItem value="Pers ayant la surveillance">Pers ayant la surveillance</MenuItem>
              <MenuItem value="custom">Autre</MenuItem>
            </Select>
          </FormControl>

          {personneVisiteeType === 'custom' && (
            <TextField
              fullWidth
              required
              label="Autre qualité de la personne"
              value={personneVisiteeTypeCustom}
              onChange={(e) => setPersonneVisiteeTypeCustom(e.target.value)}
            />
          )}

          <FormControl fullWidth required>
            <InputLabel id="policiers-presents-label">Policiers présents</InputLabel>
            <Select
              labelId="policiers-presents-label"
              multiple
              value={policiersPresents}
              onChange={(e) => setPoliciersPresents(e.target.value)}
              input={<OutlinedInput label="Policiers présents" />}
              renderValue={(selected) => (
                <Box sx={{ display: 'flex', flexWrap: 'wrap', gap: 0.5 }}>
                  {selected.map((value) => (
                    <Chip key={value} label={value} />
                  ))}
                </Box>
              )}
            >
              {intervenants.map((intervenant, index) => {
                const isSeparator = intervenant.isSeparator;
                return (
                  <MenuItem
                    key={index}
                    value={intervenant.value}
                    disabled={isSeparator}
                    sx={isSeparator ? {
                      fontWeight: 'bold',
                      backgroundColor: '#f5f5f5',
                      '&:hover': { backgroundColor: '#f5f5f5' },
                      cursor: 'default'
                    } : {}}
                  >
                    {intervenant.value}
                  </MenuItem>
                );
              })}
            </Select>
          </FormControl>

          <FormControlLabel
            control={
              <Checkbox
                checked={lieuDifferent}
                onChange={(e) => setLieuDifferent(e.target.checked)}
              />
            }
            label="Lieu visité différent du domicile de la personne qui signe"
          />

          {lieuDifferent && (
            <TextField
              fullWidth
              required
              label="Adresse visitée"
              value={adresseVisitee}
              onChange={(e) => setAdresseVisitee(e.target.value)}
            />
          )}

          <FormControlLabel
            control={
              <Checkbox
                checked={hasLimitation}
                onChange={(e) => setHasLimitation(e.target.checked)}
              />
            }
            label="Limitation"
          />

          {hasLimitation && (
            <TextField
              fullWidth
              label="Limitation"
              value={limitation}
              onChange={(e) => setLimitation(e.target.value)}
            />
          )}

          <TextField
            fullWidth
            multiline
            minRows={4}
            label="But"
            value={butText}
            onChange={(e) => setButText(e.target.value)}
          />

          <FormControl fullWidth>
            <InputLabel id="faits-select-label">Faits concernés</InputLabel>
            <Select
              labelId="faits-select-label"
              multiple
              value={selectedFaits}
              label="Faits concernés"
              onChange={(e) => setSelectedFaits(e.target.value)}
              input={<OutlinedInput label="Faits concernés" />}
              renderValue={(selected) => (
                <Box sx={{ display: 'flex', flexWrap: 'wrap', gap: 0.5 }}>
                  {selected.map((value) => (
                    <Chip key={value} label={faits.find(f => f.value === value)?.label || value} />
                  ))}
                </Box>
              )}
            >
              {faits.map((fait, index) => (
                <MenuItem key={index} value={fait.value}>
                  {fait.label}
                </MenuItem>
              ))}
            </Select>
          </FormControl>



          {isFormComplete && (
            <Box sx={{ mt: 2, textAlign: 'center' }}>
              <Button
                variant="contained"
                color="primary"
                size="large"
                startIcon={<DescriptionIcon />}
                onClick={async () => {
                  try {
                    const logoImageData = await loadLogoPng();
                    const reportNumber = buildReportNumber();
                    const fontFamily = 'Helvetica';
                    const fontSize10pt = 20;
                    const typeValue = settings.type === 'local' ? 'Locale' : settings.type === 'federal' ? 'Fédérale' : settings.type || 'N/A';

                    const buildHeader = () => {
                      const noBorders = {
                        top: { style: BorderStyle.NONE },
                        bottom: { style: BorderStyle.NONE },
                        left: { style: BorderStyle.NONE },
                        right: { style: BorderStyle.NONE }
                      };

                      const headerRows = [
                        new TableRow({
                          children: [
                            new TableCell({
                              width: { size: 12, type: WidthType.PERCENTAGE },
                              borders: noBorders,
                              children: [
                                new Paragraph({
                                  children: logoImageData
                                    ? [
                                        new ImageRun({
                                          data: logoImageData,
                                          transformation: { width: 40, height: 40 },
                                          type: 'png'
                                        })
                                      ]
                                    : []
                                })
                              ]
                            }),
                            new TableCell({
                              width: { size: 38, type: WidthType.PERCENTAGE },
                              borders: noBorders,
                              children: [
                                new Paragraph({
                                  spacing: { after: 40 },
                                  children: [
                                    new TextRun({
                                      text: `Police ${typeValue}`,
                                      font: fontFamily,
                                      size: 18
                                    })
                                  ]
                                }),
                                new Paragraph({
                                  spacing: { after: 40 },
                                  children: [
                                    new TextRun({
                                      text: `${settings.clientName || 'N/A'} - ${settings.zoneNumber || 'N/A'}`,
                                      font: fontFamily,
                                      size: 18,
                                      bold: true
                                    })
                                  ]
                                }),
                                new Paragraph({
                                  children: [
                                    new TextRun({
                                      text: `Service ${reportData.unit || ''}`,
                                      font: fontFamily,
                                      size: 18
                                    })
                                  ]
                                })
                              ]
                            }),
                            new TableCell({
                              width: { size: 50, type: WidthType.PERCENTAGE },
                              borders: noBorders,
                              children: [
                                new Paragraph({
                                  alignment: AlignmentType.RIGHT,
                                  spacing: { after: 40 },
                                  children: [
                                    new TextRun({
                                      text: `Procès-verbal ${reportData.reportType === 'subsequent' ? 'subséquent' : reportData.reportType}`,
                                      font: fontFamily,
                                      size: 18
                                    })
                                  ]
                                }),
                                new Paragraph({
                                  alignment: AlignmentType.RIGHT,
                                  spacing: { after: 40 },
                                  children: [
                                    new TextRun({
                                      text: reportNumber,
                                      font: fontFamily,
                                      size: 18,
                                      bold: true
                                    })
                                  ]
                                }),
                                new Paragraph({
                                  alignment: AlignmentType.RIGHT,
                                  children: [
                                    new TextRun({
                                      text: 'Annexe n° _____',
                                      font: fontFamily,
                                      size: 18
                                    })
                                  ]
                                })
                              ]
                            })
                          ]
                        })
                      ];

                      const headerChildren = [
                        new Table({
                          width: { size: 100, type: WidthType.PERCENTAGE },
                          borders: {
                            top: { style: BorderStyle.NONE },
                            bottom: { style: BorderStyle.NONE },
                            left: { style: BorderStyle.NONE },
                            right: { style: BorderStyle.NONE },
                            insideHorizontal: { style: BorderStyle.NONE },
                            insideVertical: { style: BorderStyle.NONE }
                          },
                          rows: headerRows
                        }),
                        new Paragraph({
                          border: {
                            bottom: {
                              color: '000000',
                              space: 1,
                              style: BorderStyle.SINGLE,
                              size: 6
                            }
                          },
                          spacing: { after: 200, before: 0, line: 20 },
                          children: [
                            new TextRun({
                              text: '',
                              size: 4
                            })
                          ]
                        })
                      ];

                      return new Header({ children: headerChildren });
                    };

                    const infoParagraph = (label, value, options = {}) => {
                      if (!value) return null;
                      const isBold = options.bold !== undefined ? options.bold : true;
                      return new Paragraph({
                        children: [
                          new TextRun({ text: label, font: fontFamily, size: fontSize10pt }),
                          new TextRun({
                            text: value,
                            font: fontFamily,
                            size: fontSize10pt,
                            bold: isBold,
                            underline: options.underline ? {} : undefined
                          })
                        ]
                      });
                    };

                    const buildNationalityParagraph = () => {
                      const nat = personneVisiteeData?.nationality;
                      const rrn = personneVisiteeData?.nationalNumber;
                      if (!nat && !rrn) return null;
                      if (nat && !rrn) {
                        return infoParagraph('Nationalité : ', nat, { bold: true });
                      }
                      if (nat && rrn) {
                        return new Paragraph({
                          children: [
                            new TextRun({ text: 'Nationalité : ', font: fontFamily, size: fontSize10pt }),
                            new TextRun({ text: nat, font: fontFamily, size: fontSize10pt, bold: true }),
                            new TextRun({ text: ' (RRN : ', font: fontFamily, size: fontSize10pt }),
                            new TextRun({ text: rrn, font: fontFamily, size: fontSize10pt, bold: true }),
                            new TextRun({ text: ')', font: fontFamily, size: fontSize10pt })
                          ]
                        });
                      }
                      return new Paragraph({
                        children: [
                          new TextRun({ text: 'RRN : ', font: fontFamily, size: fontSize10pt }),
                          new TextRun({ text: rrn || '', font: fontFamily, size: fontSize10pt, bold: true })
                        ]
                      });
                    };

                    const birthLine = personneVisiteeData?.birthdate
                      ? `${formatDateShort(personneVisiteeData.birthdate)}${personneVisiteeData.birthPlace ? ` à ${personneVisiteeData.birthPlace}` : ''}`
                      : '';

                    const identityParagraphs = [
                      personneVisiteeData
                        ? new Paragraph({
                            children: [
                              new TextRun({ text: 'Nom et prénom : ', font: fontFamily, size: fontSize10pt }),
                              new TextRun({
                                text: (personneVisiteeData.lastname || '').toUpperCase(),
                                font: fontFamily,
                                size: fontSize10pt,
                                bold: true,
                                underline: {}
                              }),
                              new TextRun({ text: ' ', font: fontFamily, size: fontSize10pt }),
                              new TextRun({
                                text: personneVisiteeData.firstname || '',
                                font: fontFamily,
                                size: fontSize10pt,
                                bold: true
                              }),
                              personneVisiteeData.otherFirstnames
                                ? new TextRun({
                                    text: ` ${personneVisiteeData.otherFirstnames}`,
                                    font: fontFamily,
                                    size: fontSize10pt
                                  })
                                : null
                            ].filter(Boolean)
                          })
                        : null,
                      infoParagraph('Date et lieu de naissance : ', birthLine),
                      infoParagraph(
                        'Sexe : ',
                        personneVisiteeData?.gender === 'M'
                          ? 'Masculin'
                          : personneVisiteeData?.gender === 'F'
                          ? 'Féminin'
                          : personneVisiteeData?.gender || ''
                      ),
                      infoParagraph('État civil : ', personneVisiteeData?.civilStatus || ''),
                      buildNationalityParagraph(),
                      infoParagraph('Profession : ', personneVisiteeData?.profession || ''),
                      infoParagraph('Domicile : ', personneVisiteeData?.residence || '')
                    ].filter(Boolean);

                    const typeDescription = (() => {
                      switch (personneVisiteeType) {
                        case 'Majeur domiciliée':
                          return `personne majeure domiciliée à l'adresse`;
                        case 'Majeur non domiciliée mais résidant':
                          return `personne majeure non domiciliée à l'adresse mais y résidant de manière effective`;
                        case 'Mineur sans majeur (kot)':
                          return `personne mineure habitant à l'adresse sans que des personnes majeures y soient domiciliées`;
                        case 'Chef de famille':
                          return `chef de famille ou de ménage de l'adresse`;
                        case 'Pers ayant la surveillance':
                          return `personne ayant la surveillance de la maison à l'adresse`;
                        case 'custom':
                          return personneVisiteeTypeCustom || '';
                        default:
                          return '';
                      }
                    })();

                    const selectedIntervenantsData = policiersPresents
                      .map((value) => intervenants.find((i) => i.value === value && !i.isSeparator))
                      .filter((item) => item && item.data);

                    const buildIntervenantRuns = () => {
                      const runs = [];
                      selectedIntervenantsData.forEach((item, index) => {
                        const grade = item.data.rank || '';
                        const article = /^[aeiouhâêîôûäëïöüàéèùAEIOUH]/i.test(grade) ? "l’" : 'le ';
                        if (index === 0) {
                          // pas de texte avant le premier intervenant
                        } else if (selectedIntervenantsData.length === 2) {
                          runs.push(new TextRun({ text: ' et ', font: fontFamily, size: fontSize10pt }));
                        } else if (index === selectedIntervenantsData.length - 1) {
                          runs.push(new TextRun({ text: ' et ', font: fontFamily, size: fontSize10pt }));
                        } else {
                          runs.push(new TextRun({ text: ', ', font: fontFamily, size: fontSize10pt }));
                        }
                        runs.push(
                          new TextRun({ text: article, font: fontFamily, size: fontSize10pt }),
                          new TextRun({ text: grade, font: fontFamily, size: fontSize10pt }),
                          new TextRun({ text: ' ', font: fontFamily, size: fontSize10pt }),
                          new TextRun({
                            text: (item.data.lastname || '').toUpperCase(),
                            font: fontFamily,
                            size: fontSize10pt,
                            bold: true
                          }),
                          new TextRun({ text: ' ', font: fontFamily, size: fontSize10pt }),
                          new TextRun({
                            text: item.data.firstname || '',
                            font: fontFamily,
                            size: fontSize10pt,
                            bold: true
                          })
                        );
                      });
                      return runs;
                    };

                    const limitationText = hasLimitation && limitation ? limitation : 'sans restriction';
                    const visitAddress = lieuDifferent ? adresseVisitee : personneVisiteeData?.residence || '';
                    const dateVisiteText = dateVisite ? formatDateLongMonth(dateVisite) : '________________';
                    const isFemale = personneVisiteeData?.gender === 'F';


                    // All unused variables (deleted) - n’deleted';
                    // l’del j’ai signé'; // Deleted switch code - n’all deleted'; // n’end of deleted code';

                    const doc = new Document({
                      sections: [
                        {
                          properties: {},
                          headers: { default: buildHeader() },
                          children: [
                            new Table({
                              width: { size: 100, type: WidthType.PERCENTAGE },
                              borders: {
                                top: { style: BorderStyle.SINGLE, size: 1, color: '000000' },
                                bottom: { style: BorderStyle.SINGLE, size: 1, color: '000000' },
                                left: { style: BorderStyle.SINGLE, size: 1, color: '000000' },
                                right: { style: BorderStyle.SINGLE, size: 1, color: '000000' }
                              },
                              rows: [
                                new TableRow({
                                  children: [
                                    new TableCell({
                                      children: [
                                        new Paragraph({
                                          alignment: AlignmentType.CENTER,
                                          children: [
                                            new TextRun({
                                              text: 'CONSENTEMENT À UNE VISITE DOMICILIAIRE',
                                              font: fontFamily,
                                              size: 28,
                                              bold: true
                                            })
                                          ]
                                        })
                                      ]
                                    })
                                  ]
                                })
                              ]
                            }),
                            new Paragraph({ text: '' }),
                            new Paragraph({
                              children: [
                                new TextRun({
                                  text: 'Début de la visite domiciliaire',
                                  font: fontFamily,
                                  size: fontSize10pt,
                                  bold: true,
                                  underline: {}
                                })
                              ]
                            }),
                            new Paragraph({ text: '' }),
                            new Paragraph({
                              children: [
                                new TextRun({
                                  text: 'Date : ',
                                  font: fontFamily,
                                  size: fontSize10pt
                                }),
                                new TextRun({
                                  text: dateVisiteText,
                                  font: fontFamily,
                                  size: fontSize10pt,
                                  bold: dateVisite ? true : false
                                })
                              ]
                            }),
                            new Paragraph({ text: '' }),
                            new Paragraph({
                              children: [
                                new TextRun({
                                  text: `Je soussign${personneVisiteeData?.gender === 'F' ? 'ée' : 'é'},`,
                                  font: fontFamily,
                                  size: fontSize10pt
                                })
                              ]
                            }),
                            ...identityParagraphs,
                            new Paragraph({ text: '' }),
                            new Paragraph({
                              children: [
                                new TextRun({
                                  text: 'agissant en qualité de ',
                                  font: fontFamily,
                                  size: fontSize10pt
                                }),
                                new TextRun({
                                  text: typeDescription,
                                  font: fontFamily,
                                  size: fontSize10pt
                                }),
                                new TextRun({
                                  text:
                                    ' et ayant la connaissance suffisante de la langue française et ne souhaitant donc pas l’intervention d’un interprète, consens formellement et de mon plein gré à ce que ',
                                  font: fontFamily,
                                  size: fontSize10pt
                                }),
                                ...buildIntervenantRuns(),
                                new TextRun({
                                  text:
                                    ` ${selectedIntervenantsData.length > 1 ? 'effectuent' : 'effectue'} immédiatement et en ma présence une visite domiciliaire de manière ininterrompue et pour la durée nécessaire dans l'habitation sise à `,
                                  font: fontFamily,
                                  size: fontSize10pt
                                }),
                                new TextRun({
                                  text: visitAddress,
                                  font: fontFamily,
                                  size: fontSize10pt,
                                  bold: true
                                }),
                                new TextRun({
                                  text: ' et en ses dépendances.',
                                  font: fontFamily,
                                  size: fontSize10pt
                                })
                              ]
                            }),
                            new Paragraph({
                              bullet: { level: 0 },
                              children: [
                                new TextRun({
                                  text: limitationText,
                                  font: fontFamily,
                                  size: fontSize10pt
                                })
                              ]
                            }),
                            new Paragraph({
                              bullet: { level: 0 },
                              children: [
                                new TextRun({
                                  text: butText,
                                  font: fontFamily,
                                  size: fontSize10pt
                                })
                              ]
                            }),
                            selectedFaits.length > 0 ? new Paragraph({
                              bullet: { level: 0 },
                              children: [
                                new TextRun({
                                  text: 'pour cause de ',
                                  font: fontFamily,
                                  size: fontSize10pt
                                }),
                                ...selectedFaits.flatMap((faitValue, index) => {
                                  const faitObj = faits.find(f => f.value === faitValue);
                                  const faitLabel = faitObj?.label || '';
                                  const faitData = faitObj?.data;
                                  const dateDesc = faitData ? formatFaitDateDescription(faitData) : '';
                                  const lieuDesc = faitData?.address ? `à ${faitData.address}` : '';
                                  
                                  const runs = [
                                    new TextRun({
                                      text: faitLabel,
                                      font: fontFamily,
                                      size: fontSize10pt,
                                      bold: true
                                    })
                                  ];
                                  
                                  // Ajouter la date si elle existe
                                  if (dateDesc) {
                                    runs.push(new TextRun({
                                      text: ` ${dateDesc}`,
                                      font: fontFamily,
                                      size: fontSize10pt
                                    }));
                                  }
                                  
                                  // Ajouter le lieu si il existe
                                  if (lieuDesc) {
                                    runs.push(new TextRun({
                                      text: ` ${lieuDesc}`,
                                      font: fontFamily,
                                      size: fontSize10pt
                                    }));
                                  }
                                  
                                  if (index < selectedFaits.length - 1) {
                                    runs.push(new TextRun({
                                      text: ' ; ',
                                      font: fontFamily,
                                      size: fontSize10pt
                                    }));
                                  }
                                  return runs;
                                })
                              ]
                            }) : null,
                            new Paragraph({
                              children: [
                                new TextRun({
                                  text:
                                    'Je sais que j’ai le droit de refuser cette autorisation mais je ne désire pas faire usage de ce droit.',
                                  font: fontFamily,
                                  size: fontSize10pt
                                })
                              ]
                            }),
                            new Paragraph({
                              children: [
                                new TextRun({
                                  text:
                                    'Je suis informé qu’une fois le consentement donné, je ne peux plus le retirer une fois la perquisition entamée.',
                                  font: fontFamily,
                                  size: fontSize10pt
                                })
                              ]
                            }),
                            new Paragraph({ text: '' }),
                            new Paragraph({
                              alignment: AlignmentType.RIGHT,
                              children: [
                                new TextRun({
                                  text: 'Signature avant visite domiciliaire',
                                  font: fontFamily,
                                  size: fontSize10pt
                                })
                              ]
                            }),
                            new Paragraph({
                              alignment: AlignmentType.RIGHT,
                              children: [
                                new TextRun({
                                  text: 'de ',
                                  font: fontFamily,
                                  size: fontSize10pt
                                }),
                                new TextRun({
                                  text: (personneVisiteeData?.lastname || '').toUpperCase(),
                                  font: fontFamily,
                                  size: fontSize10pt,
                                  bold: true,
                                  underline: {}
                                }),
                                new TextRun({
                                  text: ' ',
                                  font: fontFamily,
                                  size: fontSize10pt
                                }),
                                new TextRun({
                                  text: personneVisiteeData?.firstname || '',
                                  font: fontFamily,
                                  size: fontSize10pt,
                                  bold: true
                                })
                              ]
                            }),
                            new Paragraph({ text: '' }),
                            new Paragraph({ text: '' }),
                            new Paragraph({ text: '' }),
                            new Paragraph({ text: '' }),
                            new Paragraph({ text: '' }),
                            new Paragraph({
                              border: {
                                top: {
                                  color: '000000',
                                  space: 1,
                                  style: BorderStyle.SINGLE,
                                  size: 6
                                }
                              },
                              children: [
                                new TextRun({
                                  text: '',
                                  font: fontFamily,
                                  size: fontSize10pt
                                })
                              ]
                            }),
                            new Paragraph({
                              children: [
                                new TextRun({
                                  text: 'Fin de la visite domiciliaire',
                                  font: fontFamily,
                                  size: fontSize10pt,
                                  bold: true,
                                  underline: {}
                                })
                              ]
                            }),
                            new Paragraph({ text: '' }),
                            new Paragraph({
                              children: [
                                new TextRun({
                                  text: `Je soussign${isFemale ? 'ée' : 'é'} `,
                                  font: fontFamily,
                                  size: fontSize10pt
                                }),
                                new TextRun({
                                  text: (personneVisiteeData?.lastname || '').toUpperCase(),
                                  font: fontFamily,
                                  size: fontSize10pt,
                                  bold: true,
                                  underline: {}
                                }),
                                new TextRun({ text: ' ', font: fontFamily, size: fontSize10pt }),
                                new TextRun({
                                  text: personneVisiteeData?.firstname || '',
                                  font: fontFamily,
                                  size: fontSize10pt,
                                  bold: true
                                }),
                                new TextRun({
                                  text: ', déclare que la visite domiciliaire pour laquelle j’ai donné mon accord ci-dessus, a commencé le ',
                                  font: fontFamily,
                                  size: fontSize10pt
                                }),
                                new TextRun({
                                  text: dateVisiteText,
                                  font: fontFamily,
                                  size: fontSize10pt,
                                  bold: dateVisite ? true : false
                                }),
                                new TextRun({ text: ' à ', font: fontFamily, size: fontSize10pt }),
                                new TextRun({
                                  text: '______',
                                  font: fontFamily,
                                  size: fontSize10pt
                                }),
                                new TextRun({
                                  text: ' heures et s’est terminée le ',
                                  font: fontFamily,
                                  size: fontSize10pt
                                }),
                                new TextRun({
                                  text: dateVisiteText,
                                  font: fontFamily,
                                  size: fontSize10pt,
                                  bold: dateVisite ? true : false
                                }),
                                new TextRun({ text: ' à ', font: fontFamily, size: fontSize10pt }),
                                new TextRun({
                                  text: '______',
                                  font: fontFamily,
                                  size: fontSize10pt
                                }),
                                new TextRun({
                                  text: ' heures.',
                                  font: fontFamily,
                                  size: fontSize10pt
                                })
                              ]
                            }),
                            new Paragraph({
                              children: [
                                new TextRun({
                                  text: `J'ai assisté à la perquisition et je confirme que l'habitation a été laissée dans l'état où elle était avant la perquisition.`,
                                  font: fontFamily,
                                  size: fontSize10pt
                                })
                              ]
                            }),
                            new Paragraph({ text: '' }),
                            new Paragraph({
                              children: [
                                new TextRun({
                                  text: `☐  Tous les objets emportés sont repris à l'inventaire de saisie.`,
                                  font: fontFamily,
                                  size: fontSize10pt
                                })
                              ]
                            }),
                            new Paragraph({
                              children: [
                                new TextRun({
                                  text: `☐  Aucun objet n'a été saisi ou emporté.`,
                                  font: fontFamily,
                                  size: fontSize10pt
                                })
                              ]
                            }),
                            new Paragraph({ text: '' }),
                            new Paragraph({
                              children: [
                                new TextRun({
                                  text: `☐  Je n'ai aucune observation à formuler sur la perquisition.`,
                                  font: fontFamily,
                                  size: fontSize10pt
                                })
                              ]
                            }),
                            new Paragraph({
                              children: [
                                new TextRun({
                                  text: `☐  Je formule la remarque suivante : `,
                                  font: fontFamily,
                                  size: fontSize10pt
                                })
                              ]
                            }),
                            new Paragraph({
                              children: [
                                new TextRun({
                                  text: '.......................................................................................................................................................',
                                  font: fontFamily,
                                  size: fontSize10pt
                                })
                              ]
                            }),
                            new Paragraph({
                              children: [
                                new TextRun({
                                  text: '.......................................................................................................................................................',
                                  font: fontFamily,
                                  size: fontSize10pt
                                })
                              ]
                            }),
                            new Paragraph({ text: '' }),
                            new Paragraph({
                              alignment: AlignmentType.RIGHT,
                              children: [
                                new TextRun({
                                  text: 'Signature après visite domiciliaire',
                                  font: fontFamily,
                                  size: fontSize10pt
                                })
                              ]
                            }),
                            new Paragraph({
                              alignment: AlignmentType.RIGHT,
                              children: [
                                new TextRun({
                                  text: 'de ',
                                  font: fontFamily,
                                  size: fontSize10pt
                                }),
                                new TextRun({
                                  text: (personneVisiteeData?.lastname || '').toUpperCase(),
                                  font: fontFamily,
                                  size: fontSize10pt,
                                  bold: true,
                                  underline: {}
                                }),
                                new TextRun({
                                  text: ' ',
                                  font: fontFamily,
                                  size: fontSize10pt
                                }),
                                new TextRun({
                                  text: personneVisiteeData?.firstname || '',
                                  font: fontFamily,
                                  size: fontSize10pt,
                                  bold: true
                                })
                              ]
                            })
                          ].filter(Boolean)
                        }
                      ]
                    });

                    const blob = await Packer.toBlob(doc);
                    const fileName = `${reportNumber}-consentement-visite.docx`;
                    saveAs(blob, fileName);

                    // Log du téléchargement
                    try {
                      const token = sessionStorage.getItem('authToken');
                      const qualitePersonne = personneVisiteeType === 'custom' ? personneVisiteeTypeCustom : personneVisiteeType;
                      const faitsSelectionnesLabels = selectedFaits.map(faitValue => {
                        const faitObj = faits.find(f => f.value === faitValue);
                        return faitObj?.label || faitValue;
                      });
                      
                      await axios.post('/api/appendices/log-download', {
                        appendixType: 'CensentSearch',
                        appendixTitle: 'Consentement visite domiciliaire',
                        reportNumber: `${report?.number}/${report?.year}`,
                        formData: {
                          dateVisite: dateVisite || 'Non spécifiée',
                          personneQuiSigne: `${personneVisiteeData?.lastname?.toUpperCase()} ${personneVisiteeData?.firstname}`,
                          qualitePersonne: qualitePersonne,
                          policiersPresents: policiersPresents,
                          lieuVisite: lieuDifferent ? adresseVisitee : (personneVisiteeData?.residence || 'Domicile'),
                          lieuDifferentDuDomicile: lieuDifferent,
                          aLimitation: hasLimitation,
                          limitation: hasLimitation ? limitation : null,
                          but: butText,
                          faitsConcernes: faitsSelectionnesLabels
                        }
                      }, {
                        headers: { Authorization: `Bearer ${token}` }
                      });
                    } catch (logError) {
                      console.warn('Erreur lors du log du téléchargement:', logError);
                      // Ne pas bloquer le téléchargement en cas d'erreur de log
                    }
                  } catch (err) {
                    console.error('Erreur lors de la génération du document:', err);
                  }
                }}
                sx={{
                  minWidth: 250,
                  py: 1.5,
                  textTransform: 'none',
                  fontSize: '1rem'
                }}
              >
                Générer le document
              </Button>
            </Box>
          )}
        </Box>
      </Paper>
    </Box>
  );
}

export default CensentSearch;
